import {IPolicyApplication, ITraveller, IPremium, IQuotes, ICustomer} from '../models';
import * as moment from 'moment';
import * as uuid from 'uuid/v4';
import * as document from './documents';
import { getIncludedResource } from '../util';
import { forEach, get } from 'lodash';
import { fromJsonApi as fromChannelJsonApi } from './channels';

const dateFromApi = (date) => {
  if (!date) {
    return '';
  }

  const parsedDate = moment.utc(date, 'YYYY-MM-DD HH:mm:ss');

  return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : '';
};

const dateToApi = (date) => {
  if (! date) {
    return '';
  }

  const formattedDate = moment(date, 'DD/MM/YYYY').utcOffset(0, true).format();

  return formattedDate !== 'Invalid Date' ? formattedDate : '';
};

export function fromJsonApi(data, included = []): IPolicyApplication {
  const { id, attributes : { metadata } } = data;
  const mapTraveller = (type, traveller: any, key: number): ITraveller => {
    const data: ITraveller = {
      id: traveller.id ? traveller.id : uuid(),
      title: traveller.title,
      firstName: traveller.first_name,
      lastName: traveller.last_name,
      dob: dateFromApi(traveller.dob),
      type,
      screening: traveller.screening,
      screeningSessionId: traveller.screening_id,
      marketingOptions: {
        email: traveller.marketing_options && traveller.marketing_options.email,
        sms: traveller.marketing_options && traveller.marketing_options.sms,
        phone: traveller.marketing_options && traveller.marketing_options.phone,
        post: traveller.marketing_options && traveller.marketing_options.post,
      },
    };

    if (key === 0 && type === 'adult') {
      data.email = traveller.email;
      data.confirmEmail = traveller.confirmEmail;
      data.phone = traveller.phone1;
      data.address = traveller.address;
    }

    if (key === 0 && type === 'child') {
      data.email = traveller.email;
      data.confirmEmail = traveller.confirmEmail;
      data.phone = traveller.phone1;
      data.address = traveller.address;
    }

    return data;
  };

  const travellers = [];

  const premiums: IPremium[] = getIncludedResource(data, included, 'premiums', true).map((premium): IPremium => {
    const scheme = getIncludedResource(premium, included, 'scheme');
    const documentsResource = getIncludedResource(data, included, 'channel_scheme_documents', true);
    const documents = premium.attributes.scheme.documents;
    documentsResource.forEach((document) => {
      documents[document.attributes.short_name] = document.attributes.file_path;
    });

    return {
      id: premium.id,
      schemeType: premium.attributes.scheme.scheme_type,
      calculatedAt: premium.attributes.calculated_at,
      validUntil: premium.attributes.valid_until,
      currency: premium.attributes.currency,
      tax: premium.attributes.tax,
      exTax: premium.attributes.ex_tax,
      principal: premium.attributes.principal,
      grossExAdditional: premium.attributes.gross_ex_additional,
      gross: premium.attributes.gross,
      benefits: premium.attributes.benefits.map((benefit) => ({
        name: benefit.name,
        display: benefit.display,
        text: benefit.text,
        description: benefit.description,
        inverse: benefit.inverse,
        excess: benefit.excess,
        displayName: benefit.display_name,
        shortName: benefit.short_name,
        subBenefits: benefit.sub_benefits && benefit.sub_benefits.map((subBenefit) => ({
          excess: subBenefit.excess,
          name: subBenefit.name,
          display: subBenefit.display,
          text: subBenefit.text,
        })),
      })),
      information: premium.attributes.information && {
        standardExcess: premium.attributes.information.standard_excess,
        boltOns: premium.attributes.information.bolt_ons,
        availableOptions: premium.attributes.information.available_options,
        upgrade: premium.attributes.information.upgrade && {
          schemeId: premium.attributes.information.upgrade.scheme,
          amount: premium.attributes.information.upgrade.amount,
          coverLevel: premium.attributes.information.upgrade.coverLevel,
        },
        maxTripDuration: premium.attributes.information.max_trip_duration,
        startDate: premium.attributes.information.start_date,
        region: premium.attributes.information.region,
        tripType: premium.attributes.information.scheme_type,
      },
      declines: premium.attributes.declines && premium.attributes.declines.map((decline) => (
          {
            name: decline.name,
            reason: decline.reason,
            isMedical: !!decline.is_medical,
          }
      )),
      referrals: premium.attributes.referrals && premium.attributes.referrals.map((referral) => (
          {
            name: referral.name,
            reason: referral.reason,
            isMedical: !!referral.is_medical,
          }
      )),
      errors: premium.attributes.errors && premium.attributes.errors,
      isSignposted: premium.attributes.is_signposted,
      scheme: {
        id: scheme.id,
        coverLevel: scheme.attributes.cover_level,
        status: scheme.attributes.status,
        logo: scheme.attributes.logo,
        name: scheme.attributes.name,
        insurer: scheme.attributes.insurer,
        documents,
        includedOptions: scheme.attributes.included_options || [],
      },
    };
  });

  const customer: ICustomer = getIncludedResource(data, included, 'customer', true).map((customer): ICustomer => {

    return {
      id: customer.id,
      title: customer.attributes.title,
      firstName: customer.attributes.first_name,
      lastName: customer.attributes.last_name,
      companyName: customer.attributes.company_name,
      customerName: customer.attributes.customer_name,
      dob: dateFromApi(customer.attributes.dob),
      address: customer.attributes.address,
      email: customer.attributes.email,
      phone1: customer.attributes.phone1,
      phone2: customer.attributes.phone2,
      status: customer.attributes.status,
      deceased: customer.attributes.deceased,
      metadata: customer.attributes.metadata,
      policies: customer.policies,
      policyApplications: customer.policy_applications,
      marketingOptions: {
        email: customer.attributes.marketing_options.email,
        sms: customer.attributes.marketing_options.sms,
        phone: customer.attributes.marketing_options.phone,
        post: customer.attributes.marketing_options.post,
      },
      renewals: [],
    };
  });

  const quotes: IQuotes = {};

  premiums.forEach((premium) => {
    if (!quotes[premium.scheme.coverLevel]) {
      quotes[premium.scheme.coverLevel] = {};
    }

    quotes[premium.scheme.coverLevel][premium.schemeType] = premium;
  });

  if (metadata.adults) {
    metadata.adults.forEach((adult, key) => {
      travellers.push(mapTraveller('adult', adult, key));
    });
  }

  if (metadata.children) {
    metadata.children.forEach((child, key) => {
      travellers.push(mapTraveller('child', child, key));
    });
  }

  const quotePremiums = getIncludedResource(data, included, 'policies/premiums', true)
    .map((item) => quotePremiums.fromJsonApi(item, included));

  const documents = getIncludedResource(data, included, 'documents', true)
    .map((item) => document.fromJsonApi(item, included));

  const channel = ((channel) => {
    if (! channel) {
      return;
    }

    return fromChannelJsonApi(channel, included);
  })(getIncludedResource(data, included, 'channel'));

  // noinspection UnnecessaryLocalVariableJS
  const policyApplication: IPolicyApplication = {
    productCode: metadata.product_code,
    quoteType: data.attributes.quote_type,
    quotationTypes: metadata.quotation_types,
    residency: metadata.residency,
    quotationDeclaration: metadata.quotation_declaration,
    tripType: metadata.scheme_type,
    destinations: metadata.destinations,
    startDate: dateFromApi(metadata.start_date),
    endDate: dateFromApi(metadata.end_date),
    endsAt: dateFromApi(data.attributes.ends_at),
    maxTripDuration: parseInt(metadata.max_trip_duration, 10),
    groupType: metadata.traveller_group,
    travellers,
    campaignCode: metadata.campaign_code,
    region: metadata.region,
    options: metadata.options,
    quotes: metadata.quotes,
    medicalDeclaration: metadata.medical_declaration_questions,
    steps: metadata.steps,
    customer,
    schemeId: metadata.scheme_id,
    quoteReference: data.attributes.quote_reference,
    status: data.attributes.status ? data.attributes.status : null,
    insured: data.attributes.insured,
    quotePremiums,
    premiums,
    generateDocuments: false,
    emailSend: metadata.send_email,
    documents,
    channel,
    calculateAllSchemes: false,
    whereDidYouHearAboutUs: metadata.marketing_referral,
    purchaseChannel: metadata.purchase_channel ? metadata.purchase_channel : null,
    activities: metadata.activities ? metadata.activities : null,
    ageVehicle: metadata.age_vehicle ? metadata.age_vehicle : null,
    vehicleReg: metadata.vehicle_reg ? metadata.vehicle_reg : null,
    companyName: metadata.company_name ? metadata.company_name : null,
    make: metadata.make ? metadata.make : null,
    model: metadata.model ? metadata.model : null,
    type: metadata.type ? metadata.type : null,
    year: metadata.year ? metadata.year : null,
    value: metadata.value ? metadata.value : null,
    colour: metadata.colour ? metadata.colour : null,
    transType: metadata.trans_type ? metadata.trans_type : null,
    fuelType: metadata.fuel_type ? metadata.fuel_type : null,
    fourByFourDrive: metadata.four_by_four_drive ? metadata.four_by_four_drive : null,
    signposted: metadata.signposted,
    reuseCustomerAddress: metadata.reuseCustomerAddress,
  };

  if (id) {
    policyApplication.id = id;
  }

  if (quotes) {
    policyApplication.quotes = quotes;
  }

  return policyApplication;
}

export function toJsonApi(
  policyApplication: IPolicyApplication,
  channelId?: string,
  brokerId?: string,
  productCode?: string,
) {
  const postcode = get(policyApplication, 'travellers.0.address.postcode', '');
  let residencyNew = 'uk';
  if (postcode !== '') {
    const prefix = postcode.slice(0, 2).toUpperCase();

    if (prefix === 'JE' || prefix === 'GY') {
      residencyNew = 'channel islands';
    } else if (prefix === 'IM') {
      residencyNew = 'isle of man';
    }
  }
  const mapTraveller = (type) => (traveller: ITraveller, key: number) => {
    const data: any = {
      metadata: {},
      id: traveller.id,
      title: traveller.title,
      first_name: traveller.firstName,
      last_name: traveller.lastName,
      dob: dateToApi(traveller.dob),
      screening: traveller.screening,
      screening_id: traveller.screeningSessionId,
      conditions_covered: true,
      marketing_options: {
        email: traveller.marketingOptions && traveller.marketingOptions.email,
        sms: traveller.marketingOptions && traveller.marketingOptions.sms,
        phone: traveller.marketingOptions && traveller.marketingOptions.phone,
        post: traveller.marketingOptions && traveller.marketingOptions.post,
      },
    };

    if (key === 0 && type === 'adult') {
      data.email = traveller.email;
      data.confirmEmail = traveller.confirmEmail;
      data.phone1 = traveller.phone;
      data.address = traveller.address;
    }

    if (key === 0 && type === 'child' && policyApplication.groupType === 'independent_child') {
      data.email = traveller.email;
      data.confirmEmail = traveller.confirmEmail;
      data.phone1 = traveller.phone;
      data.address = traveller.address;
    }

    return data;
  };

  const metadata: any = {
    product_code: productCode || policyApplication.productCode,
    scheme_type: policyApplication.tripType,
    quotation_types: policyApplication.quotationTypes,
    sales_channel: policyApplication.salesChannel,
    signposted: policyApplication.signposted,
    reuseCustomerAddress: policyApplication.reuseCustomerAddress,
    residency: residencyNew,
    traveller_group: policyApplication.groupType,
    destinations: policyApplication.destinations,
    campaign_code: policyApplication.campaignCode,
    region: policyApplication.region ? policyApplication.region : '',
    start_date: dateToApi(policyApplication.startDate),
    end_date: dateToApi(policyApplication.endDate),
    max_trip_duration: policyApplication.maxTripDuration,
    adults: policyApplication.travellers.filter(({ type }) => type === 'adult').map(mapTraveller('adult')),
    children: policyApplication.travellers.filter(({ type }) => type === 'child').map(mapTraveller('child')),
    options: policyApplication.options,
    medical_declaration_questions: policyApplication.medicalDeclaration,
    quotation_declaration: policyApplication.quotationDeclaration,
    scheme_id: policyApplication.schemeId,
    policy_declaration: policyApplication.policyDeclaration,
    cancel_medical_declaration: policyApplication.cancelMedicalDeclaration,
    generate_documents: policyApplication.generateDocuments,
    send_email: policyApplication.emailSend,
    steps: policyApplication.steps,
    marketing_referral: policyApplication.whereDidYouHearAboutUs,
    purchase_channel: policyApplication.purchaseChannel ? policyApplication.purchaseChannel : null,
    activities: policyApplication.activities ? policyApplication.activities : null,
    age_vehicle: policyApplication.ageVehicle ? policyApplication.ageVehicle : null,
    vehicle_reg: policyApplication.vehicleReg ? policyApplication.vehicleReg : null,
    company_name: policyApplication.companyName ? policyApplication.companyName : null,
    make: policyApplication.make ? policyApplication.make : null,
    model: policyApplication.model ? policyApplication.model : null,
    type: policyApplication.type ? policyApplication.type : null,
    year: policyApplication.year ? policyApplication.year : null,
    value: policyApplication.value ? policyApplication.value : null,
    colour: policyApplication.colour ? policyApplication.colour : null,
    trans_type: policyApplication.transType ? policyApplication.transType : null,
    fuel_type: policyApplication.fuelType ? policyApplication.fuelType : null,
    four_by_four_drive: policyApplication.fourByFourDrive ? policyApplication.fourByFourDrive : null,
  };

  const meta = {
    customer: {
      create_account: policyApplication.createAccount ? policyApplication.createAccount : false,
      password: policyApplication.password,
      password_confirmation: policyApplication.passwordConfirmation,
    },
  };

  forEach(policyApplication.options, (value, key) => {
    if (value === 'true') {
      value = true;
    }

    if (value === 'false') {
      value = false;
    }

    metadata.options[key] = value;
  });

  // noinspection UnnecessaryLocalVariableJS
  const data: any = {
    type: 'application',
    attributes: {
      metadata,
      quote_reference: policyApplication.quoteReference,
      quote_type: policyApplication.quoteType,
    },
    meta,
    relationships: {},
  };

  if (policyApplication.schemeId && ! policyApplication.calculateAllSchemes) {
    data.relationships.schemes = {
      data: [
        { id: policyApplication.schemeId },
      ],
    };
  }

  if (policyApplication.id) {
    data.id = policyApplication.id;
  }

  if (channelId) {
    data.relationships.channel = {
      data: {
        id: channelId,
      },
    };
  }

  if (brokerId) {
    data.relationships.broker = {
      data: {
        id: brokerId,
      },
    };
  }

  return data;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/transformers/application.ts